// src/components/news/NewsSourcesList.js - News sources list with management
import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useNotification } from '../../hooks/useNotification';
import NewsSourceCard from './NewsSourceCard';
import NewsSourceForm from './NewsSourceForm';
import { getErrorMessage } from '../../utils/error';

const NewsSourcesList = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [fetchingAll, setFetchingAll] = useState(false);
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    loadSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSources = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/news/sources');
      setSources(response.data.sources || []);
    } catch (error) {
      console.error('Failed to load news sources:', error);
      showError('Failed to load news sources');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (source) => {
    setEditingSource(source);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingSource(null);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingSource(null);
  };

  const handleSuccess = () => {
    loadSources();
  };

  const handleFetch = async (sourceId) => {
    try {
      await api.post(`/api/news/sources/${sourceId}/fetch`);
      showSuccess('RSS feed fetched successfully');
      loadSources();
    } catch (error) {
      console.error('Failed to fetch RSS feed:', error);
      showError(getErrorMessage(error, 'Failed to fetch RSS feed'));
    }
  };

  const handleFetchAll = async () => {
    setFetchingAll(true);
    try {
      const response = await api.post('/api/news/sources/fetch-all');
      showSuccess(`Fetched ${response.data.successful} of ${response.data.total_sources} sources`);
      loadSources();
    } catch (error) {
      console.error('Failed to fetch all RSS feeds:', error);
      showError('Failed to fetch all RSS feeds');
    } finally {
      setFetchingAll(false);
    }
  };

  const handleToggle = async (sourceId, isActive) => {
    try {
      // TODO: Implement toggle endpoint
      // For now, we'll update locally
      setSources(sources.map(s => 
        s.id === sourceId ? { ...s, is_active: !s.is_active } : s
      ));
      showSuccess(`Source ${isActive ? 'deactivated' : 'activated'}`);
    } catch (error) {
      console.error('Failed to toggle source:', error);
      showError('Failed to toggle source status');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900">News Sources ({sources.length})</h2>
          <div className="flex gap-2">
            <button
              onClick={handleFetchAll}
              disabled={fetchingAll}
              className="px-4 py-2 text-sm text-white bg-green-600 dark:bg-green-700 rounded-lg hover:bg-green-700 dark:hover:bg-green-800 disabled:opacity-50 transition-colors"
            >
              {fetchingAll ? 'Fetching...' : 'Fetch All RSS Feeds'}
            </button>
            <button
              onClick={handleCreate}
              className="px-4 py-2 text-sm text-white bg-shayek-primary dark:bg-shayek-primary-600 rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light transition-colors shadow-shayek"
            >
              + Add Source
            </button>
          </div>
        </div>
      </div>

      {/* Sources List */}
      {loading ? (
        <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow p-6 text-center text-gray-500 dark:text-shayek-dark-500 border border-shayek-light-300 dark:border-shayek-dark-200">
          Loading news sources...
        </div>
      ) : sources.length === 0 ? (
        <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow p-6 text-center text-gray-500 dark:text-shayek-dark-500 border border-shayek-light-300 dark:border-shayek-dark-200">
          No news sources found. Click "Add Source" to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sources.map((source) => (
            <NewsSourceCard
              key={source.id}
              source={source}
              onEdit={handleEdit}
              onFetch={handleFetch}
              onToggle={handleToggle}
            />
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <NewsSourceForm
          source={editingSource}
          onClose={handleCloseForm}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};

export default NewsSourcesList;

